<script setup>
import { computed } from 'vue';

const props = defineProps({ data: { type: [Object, Array], required: true } });

const labels = {
  id: '记录编号', service: '服务', operation: '操作', requestId: '上游请求号', status: '状态', httpStatus: 'HTTP 状态',
  errorCode: '错误码', latency: '耗时', at: '时间', request: '请求参数', response: '响应结果', resultCode: '结果码',
  message: '消息', apiId: 'API 请求号', currency: '币种', amount: '金额', cardNo: '卡号标识', customerNo: '客户号',
  timestamp: '时间戳', sign: '签名', data: '业务数据', groupNo: '卡组编号', cardName: '卡片名称', email: '邮箱'
};

function title(value) {
  const key = String(value || '');
  if (labels[key]) return labels[key];
  return key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replaceAll('_', ' ').replace(/^./, (item) => item.toUpperCase());
}

function scalar(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? '是' : '否';
  return String(value);
}

function rows(value, prefix = '', depth = 0) {
  if (depth > 5) return [{ label: prefix || '内容', value: '[内容层级过深]' }];
  if (Array.isArray(value)) {
    if (!value.length) return [{ label: prefix || '内容', value: '空列表' }];
    return value.flatMap((item, index) => {
      const next = `${prefix ? `${prefix} · ` : ''}#${index + 1}`;
      return item && typeof item === 'object' ? rows(item, next, depth + 1) : [{ label: next, value: scalar(item) }];
    });
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value);
    if (!entries.length) return [{ label: prefix || '内容', value: '空对象' }];
    return entries.flatMap(([key, item]) => {
      const next = `${prefix ? `${prefix} · ` : ''}${title(key)}`;
      return item && typeof item === 'object' ? rows(item, next, depth + 1) : [{ label: next, value: scalar(item) }];
    });
  }
  return [{ label: prefix || '内容', value: scalar(value) }];
}

const cards = computed(() => {
  const source = Array.isArray(props.data) ? { items: props.data } : props.data;
  return Object.entries(source || {}).map(([key, value]) => ({
    key,
    title: title(key),
    rows: value && typeof value === 'object' ? rows(value) : [{ label: title(key), value: scalar(value) }]
  }));
});
</script>

<template>
  <div class="credential-cards">
    <article v-for="card in cards" :key="card.key" :class="{ wide: card.rows.length > 3 }">
      <header><span></span><strong>{{ card.title }}</strong><small>{{ card.rows.length }} 项</small></header>
      <dl><div v-for="(row,index) in card.rows" :key="`${card.key}-${index}`"><dt>{{ row.label }}</dt><dd>{{ row.value }}</dd></div></dl>
    </article>
  </div>
</template>

<style scoped>
.credential-cards{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:11px;max-height:58vh;overflow:auto;padding:2px}.credential-cards article{min-width:0;overflow:hidden;border:1px solid #e3e7ed;border-radius:13px;background:#fbfcfe}.credential-cards article.wide{grid-column:1/-1}.credential-cards header{padding:11px 13px;display:flex;align-items:center;gap:8px;border-bottom:1px solid #e9ecf1;background:#f4f6fa}.credential-cards header>span{width:8px;height:8px;border-radius:50%;background:#6049ee;box-shadow:0 0 0 4px #e7e2ff}.credential-cards header strong{flex:1;color:#202a3c;font-size:11px}.credential-cards header small{color:#929baa;font-size:8px}.credential-cards dl{margin:0;padding:4px 13px}.credential-cards dl div{padding:8px 0;display:grid;grid-template-columns:minmax(90px,.8fr) minmax(0,1.2fr);gap:10px;border-bottom:1px solid #edf0f4}.credential-cards dl div:last-child{border:0}.credential-cards dt{color:#8993a3;font-size:9px}.credential-cards dd{margin:0;color:#283347;font-family:ui-monospace,SFMono-Regular,Consolas,monospace;font-size:10px;font-weight:650;overflow-wrap:anywhere;text-align:right}@media(max-width:560px){.credential-cards{grid-template-columns:1fr}.credential-cards article.wide{grid-column:auto}.credential-cards dl div{grid-template-columns:1fr}.credential-cards dd{text-align:left}}
</style>
